import type { Locale } from '@/lib/i18n/locale';
import { SITE_SCHEMA_IDS } from './structured-data';
import { SITE_ICON_PATH, SITE_URL } from './site';
import { GOOGLE_PLAY_URL } from './app-links';

export { GOOGLE_PLAY_URL } from './app-links';
export const downloadPath = (locale: Locale) => `/${locale}/download`;
// Actual substantive copy update; never replace this with a request-time date.
export const DOWNLOAD_UPDATED_AT = '2026-10-08T00:00:00.000Z';

type DownloadCopy = {
  metaTitle: string;
  description: string;
  eyebrow: string;
  title: string;
  highlight: string;
  storeCta: string;
  homeCta: string;
  browserCta: string;
  homeLabel: string;
  languagesLabel: string;
  free: string;
  accountNote: string;
  featuresTitle: string;
  features: Array<{ title: string; body: string }>;
  privacy: string;
  terms: string;
  officialNote: string;
  rankedScreenshot: string;
  rankedAlt: string;
  rankedCaption: string;
  installTitle: string;
  installSteps: string[];
  identityTitle: string;
  publisherLabel: string;
  packageLabel: string;
  questionsTitle: string;
  questions: Array<{ question: string; answer: string }>;
};

export const DOWNLOAD_COPY: Record<Locale, DownloadCopy> = {
  en: {
    metaTitle: 'Quizball on Google Play — Official Android Download',
    description: 'Download the official Quizball football and soccer quiz app on Google Play. Play 1v1 trivia, Tiki Taka Toe, Football Auction and daily challenges on Android.',
    eyebrow: 'The official Quizball Android app',
    title: 'Quizball for Android.',
    highlight: 'Football in your pocket.',
    storeCta: 'Get Quizball on Google Play',
    homeCta: 'Quizball on Google Play',
    browserCta: 'Play in your browser',
    homeLabel: 'Quizball home',
    languagesLabel: 'Choose language',
    free: 'Free download · Android phones and tablets',
    accountNote: 'Play supported solo games as a guest in the app or your browser. Create an account for ranked matches, ranking points, multiplayer and saved progress.',
    featuresTitle: 'More than a football quiz',
    features: [
      { title: 'Ranked 1v1 trivia', body: 'Answer football questions, compete head-to-head and climb the leaderboard.' },
      { title: 'Tiki Taka Toe & Auction', body: 'Name the player who fits both categories, or use the clues to win a football signing.' },
      { title: 'Daily games & Party Quiz', body: 'Try daily football challenges or invite your friends to a private quiz lobby.' },
    ],
    privacy: 'Privacy policy', terms: 'Terms of service',
    officialNote: 'The official Quizball app from quizball.io, published by Quizball LLC.',
    rankedScreenshot: '/assets/download/ranked-en.png',
    rankedAlt: 'Quizball Ranked gameplay on an Android phone: football possession, a question and four answers.',
    rankedCaption: 'Ranked 1v1. Real Android gameplay.',
    installTitle: 'How to download Quizball from Google Play',
    installSteps: ['Open the official Google Play listing using the button above.', 'Check that the developer is Quizball, then tap Install on your Android phone or tablet.', 'Open Quizball, choose your language and play. Sign in when you want ranked matches and saved progress.'],
    identityTitle: 'The Quizball app from quizball.io',
    publisherLabel: 'Published by',
    packageLabel: 'Android app ID',
    questionsTitle: 'Before you install',
    questions: [
      { question: 'Which Quizball app is this?', answer: 'This is the official Android app of quizball.io, published by Quizball LLC. The Google Play developer name is Quizball and the Android app ID is io.quizball.mobile. Use the download button on this page to open the correct listing.' },
      { question: 'Is Quizball free to download?', answer: 'Yes. Quizball is free to download from Google Play for supported Android phones and tablets. An internet connection is required to play.' },
      { question: 'Which languages can I choose?', answer: 'The Quizball app supports English, Georgian, Spanish and Turkish. Choose your language in the app’s settings.' },
    ],
  },
  ka: {
    metaTitle: 'Quizball Android-ზე — ჩამოტვირთე Google Play-დან',
    description: 'ჩამოტვირთე Quizball-ის ოფიციალური საფეხბურთო ქვიზი Google Play-დან. ითამაშე 1v1 მატჩები, Tiki Taka Toe, აუქციონი და ყოველდღიური გამოწვევები Android-ზე.',
    eyebrow: 'Quizball-ის ოფიციალური Android აპი',
    title: 'Quizball Android-ზე.',
    highlight: 'ფეხბურთი შენს ჯიბეში.',
    storeCta: 'ჩამოტვირთე Google Play-დან',
    homeCta: 'Quizball Google Play-ზე',
    browserCta: 'ითამაშე ბრაუზერში',
    homeLabel: 'Quizball-ის მთავარი გვერდი',
    languagesLabel: 'აირჩიე ენა',
    free: 'უფასო ჩამოტვირთვა · Android ტელეფონები და პლანშეტები',
    accountNote: 'ხელმისაწვდომი სოლო თამაშები სტუმრად ითამაშე აპში ან ბრაუზერში. სარეიტინგო მატჩებისთვის, სარეიტინგო ქულებისთვის, მეგობრებთან თამაშისთვის და პროგრესის შესანახად შექმენი ანგარიში.',
    featuresTitle: 'მეტი, ვიდრე საფეხბურთო ქვიზი',
    features: [
      { title: 'სარეიტინგო 1v1 მატჩები', body: 'უპასუხე საფეხბურთო კითხვებს, შეეჯიბრე მეტოქეს და დაწინაურდი ლიდერბორდში.' },
      { title: 'Tiki Taka Toe და აუქციონი', body: 'დაასახელე ფეხბურთელი, რომელიც ორივე კატეგორიას ერგება, ან მინიშნებებით მოიგე აუქციონი.' },
      { title: 'ყოველდღიური თამაშები და Party Quiz', body: 'სცადე ყოველდღიური საფეხბურთო გამოწვევები ან მოიწვიე მეგობრები პირად ქვიზ-ოთახში.' },
    ],
    privacy: 'კონფიდენციალურობის პოლიტიკა', terms: 'მომსახურების პირობები',
    officialNote: 'Quizball-ის ოფიციალური აპი quizball.io-დან. გამომცემელი: Quizball LLC.',
    rankedScreenshot: '/assets/download/ranked-en.png',
    rankedAlt: 'Quizball-ის რეიტინგული თამაში Android ტელეფონზე: ბურთის ფლობა, კითხვა და ოთხი პასუხი. ინგლისური ვერსია.',
    rankedCaption: 'რეიტინგული 1v1 Android-ზე · ინგლისური ვერსია',
    installTitle: 'როგორ ჩამოტვირთო Quizball Google Play-დან',
    installSteps: ['ზემოთ მოცემული ღილაკით გახსენი ოფიციალური გვერდი Google Play-ზე.', 'შეამოწმე, რომ დეველოპერის სახელია Quizball, შემდეგ დააჭირე Install-ს შენს Android ტელეფონზე ან პლანშეტზე.', 'გახსენი Quizball, აირჩიე ენა და ითამაშე. სარეიტინგო მატჩებისთვის და პროგრესის შესანახად შედი ანგარიშში.'],
    identityTitle: 'Quizball-ის აპი quizball.io-დან',
    publisherLabel: 'გამომცემელი',
    packageLabel: 'Android აპის ID',
    questionsTitle: 'ჩამოტვირთვამდე',
    questions: [
      { question: 'რომელი Quizball აპია ეს?', answer: 'ეს არის quizball.io-ს ოფიციალური Android აპი, რომლის გამომცემელია Quizball LLC. Google Play-ზე დეველოპერის სახელია Quizball, ხოლო Android აპის ID — io.quizball.mobile. სწორი გვერდის გასახსნელად გამოიყენე ამ გვერდის ჩამოტვირთვის ღილაკი.' },
      { question: 'Quizball-ის ჩამოტვირთვა უფასოა?', answer: 'დიახ. Quizball Google Play-დან უფასოდ ჩამოიტვირთება მხარდაჭერილ Android ტელეფონებსა და პლანშეტებზე. თამაშს ინტერნეტკავშირი სჭირდება.' },
      { question: 'რომელი ენები შემიძლია ავირჩიო?', answer: 'Quizball-ის აპში ხელმისაწვდომია ინგლისური, ქართული, ესპანური და თურქული. ენა აპის პარამეტრებში აირჩიე.' },
    ],
  },
  es: {
    metaTitle: 'Quizball para Android — Descarga oficial en Google Play',
    description: 'Descarga la app oficial de Quizball en Google Play. Trivia de fútbol 1v1, Tiki Taka Toe, subastas y retos diarios en tu móvil o tablet Android.',
    eyebrow: 'La app oficial de Quizball para Android',
    title: 'Quizball para Android.',
    highlight: 'El fútbol en tu bolsillo.',
    storeCta: 'Descarga Quizball en Google Play',
    homeCta: 'Quizball en Google Play',
    browserCta: 'Juega en tu navegador',
    homeLabel: 'Inicio de Quizball',
    languagesLabel: 'Elige tu idioma',
    free: 'Descarga gratuita · Móviles y tablets Android',
    accountNote: 'Juega los modos individuales disponibles como invitado en la app o en el navegador. Crea una cuenta para jugar partidos de clasificación, ganar puntos, jugar en multijugador y guardar tu progreso.',
    featuresTitle: 'Más que un quiz de fútbol',
    features: [
      { title: 'Trivia 1v1 con clasificación', body: 'Responde preguntas de fútbol, compite cara a cara y sube en la clasificación.' },
      { title: 'Tiki Taka Toe y subasta', body: 'Adivina el jugador que encaja en ambas categorías o usa las pistas para ganar un fichaje.' },
      { title: 'Retos diarios y Party Quiz', body: 'Prueba los retos diarios de fútbol o invita a tus amigos a una sala privada de trivia.' },
    ],
    privacy: 'Política de privacidad', terms: 'Términos del servicio',
    officialNote: 'La app oficial de Quizball de quizball.io, publicada por Quizball LLC.',
    rankedScreenshot: '/assets/download/ranked-es.png',
    rankedAlt: 'Una partida clasificatoria de Quizball en un móvil Android: posesión, una pregunta y cuatro respuestas.',
    rankedCaption: 'Clasificatorio 1v1. Partida real en Android.',
    installTitle: 'Cómo descargar Quizball desde Google Play',
    installSteps: ['Abre la ficha oficial de Google Play con el botón de arriba.', 'Comprueba que el desarrollador es Quizball y pulsa Instalar en tu móvil o tablet Android.', 'Abre Quizball, elige tu idioma y juega. Inicia sesión para jugar partidos de clasificación y guardar tu progreso.'],
    identityTitle: 'La app de Quizball de quizball.io',
    publisherLabel: 'Publicada por',
    packageLabel: 'ID de la app Android',
    questionsTitle: 'Antes de instalar',
    questions: [
      { question: '¿Qué app de Quizball es esta?', answer: 'Es la app oficial para Android de quizball.io, publicada por Quizball LLC. El nombre del desarrollador en Google Play es Quizball y el ID de la app Android es io.quizball.mobile. Usa el botón de descarga de esta página para abrir la ficha correcta.' },
      { question: '¿Se puede descargar Quizball gratis?', answer: 'Sí. Quizball se descarga gratis desde Google Play en móviles y tablets Android compatibles. Necesitas conexión a internet para jugar.' },
      { question: '¿Qué idiomas puedo elegir?', answer: 'La app de Quizball está disponible en inglés, georgiano, español y turco. Puedes elegir el idioma en los ajustes de la app.' },
    ],
  },
  tr: {
    metaTitle: 'Android için Quizball — Google Play’den resmî indirme',
    description: 'Quizball’un resmî futbol bilgi oyununu Google Play’den indir. Android’de 1v1 bilgi yarışması, Tiki Taka Toe, futbol açık artırması ve günlük görevler oyna.',
    eyebrow: 'Quizball’un resmî Android uygulaması',
    title: 'Android için Quizball.',
    highlight: 'Futbol cebinde.',
    storeCta: 'Quizball’u Google Play’den indir',
    homeCta: 'Google Play’de Quizball',
    browserCta: 'Tarayıcıda oyna',
    homeLabel: 'Quizball ana sayfası',
    languagesLabel: 'Dil seç',
    free: 'Ücretsiz indir · Android telefonlar ve tabletler',
    accountNote: 'Uygulamada veya tarayıcıda desteklenen tek oyunculu modları misafir olarak oyna. Dereceli maçlar, sıralama puanları, çok oyunculu oyunlar ve ilerlemeni kaydetmek için hesap oluştur.',
    featuresTitle: 'Bir futbol quizinden daha fazlası',
    features: [
      { title: 'Dereceli 1v1 bilgi yarışması', body: 'Futbol sorularını yanıtla, rakibinle bire bir yarış ve liderlik tablosunda yüksel.' },
      { title: 'Tiki Taka Toe ve açık artırma', body: 'İki kategoriye de uyan futbolcuyu bul veya ipuçlarını kullanarak transferi kazan.' },
      { title: 'Günlük görevler ve Party Quiz', body: 'Günlük futbol görevlerini dene veya arkadaşlarını özel bir quiz lobisine davet et.' },
    ],
    privacy: 'Gizlilik politikası', terms: 'Hizmet şartları',
    officialNote: 'Quizball LLC tarafından yayınlanan, quizball.io’nun resmî Quizball uygulaması.',
    rankedScreenshot: '/assets/download/ranked-tr.png',
    rankedAlt: 'Android telefonda Quizball dereceli maçı: topa sahip olma, bir soru ve dört yanıt.',
    rankedCaption: 'Dereceli 1v1. Android’de gerçek oyun.',
    installTitle: 'Quizball Google Play’den nasıl indirilir?',
    installSteps: ['Yukarıdaki düğmeyle resmî Google Play sayfasını aç.', 'Geliştirici adının Quizball olduğunu kontrol et, ardından Android telefonunda veya tabletinde Yükle’ye dokun.', 'Quizball’u aç, dilini seç ve oyna. Dereceli maçlar ve ilerlemeni kaydetmek için giriş yap.'],
    identityTitle: 'quizball.io’nun Quizball uygulaması',
    publisherLabel: 'Yayıncı',
    packageLabel: 'Android uygulama kimliği',
    questionsTitle: 'Yüklemeden önce',
    questions: [
      { question: 'Bu hangi Quizball uygulaması?', answer: 'Bu, quizball.io’nun Quizball LLC tarafından yayınlanan resmî Android uygulamasıdır. Google Play’de geliştirici adı Quizball, Android uygulama kimliği ise io.quizball.mobile’dır. Doğru sayfayı açmak için bu sayfadaki indirme düğmesini kullan.' },
      { question: 'Quizball ücretsiz indirilebilir mi?', answer: 'Evet. Quizball, desteklenen Android telefonlara ve tabletlere Google Play’den ücretsiz indirilebilir. Oynamak için internet bağlantısı gerekir.' },
      { question: 'Hangi dilleri seçebilirim?', answer: 'Quizball uygulaması İngilizce, Gürcüce, İspanyolca ve Türkçeyi destekler. Dilini uygulamanın ayarlarından seçebilirsin.' },
    ],
  },
  ru: {
    metaTitle: 'Quizball в Google Play — официальная загрузка для Android',
    description: 'Скачай официальное приложение Quizball с футбольными викторинами в Google Play. Играй в викторины 1 на 1, Tiki Taka Toe, футбольный аукцион и ежедневные испытания на Android.',
    eyebrow: 'Официальное приложение Quizball для Android',
    title: 'Quizball для Android.',
    highlight: 'Футбол всегда под рукой.',
    storeCta: 'Скачать Quizball в Google Play',
    homeCta: 'Quizball в Google Play',
    browserCta: 'Играть в браузере',
    homeLabel: 'Главная Quizball',
    languagesLabel: 'Выбери язык',
    free: 'Бесплатная загрузка · Телефоны и планшеты Android',
    accountNote: 'Играй в доступные одиночные режимы как гость в приложении или браузере. Создай аккаунт, чтобы играть рейтинговые матчи, получать очки рейтинга, играть по сети и сохранять прогресс.',
    featuresTitle: 'Больше, чем футбольная викторина',
    features: [
      { title: 'Рейтинговая викторина 1 на 1', body: 'Отвечай на вопросы о футболе, соревнуйся с соперником и поднимайся в рейтинге.' },
      { title: 'Tiki Taka Toe и аукцион', body: 'Назови футболиста, который подходит обеим категориям, или используй подсказки, чтобы выиграть трансфер.' },
      { title: 'Ежедневные игры и Party Quiz', body: 'Попробуй ежедневные футбольные испытания или пригласи друзей в закрытую комнату викторины.' },
    ],
    privacy: 'Политика конфиденциальности', terms: 'Условия использования',
    officialNote: 'Официальное приложение Quizball от quizball.io, издатель — Quizball LLC.',
    rankedScreenshot: '/assets/download/ranked-en.png',
    rankedAlt: 'Рейтинговая игра Quizball на телефоне Android: владение мячом, вопрос и четыре варианта ответа.',
    rankedCaption: 'Рейтинговая игра 1 на 1. Настоящий геймплей на Android.',
    installTitle: 'Как скачать Quizball из Google Play',
    installSteps: ['Открой официальную страницу Google Play с помощью кнопки выше.', 'Убедись, что разработчик — Quizball, затем нажми «Установить» на телефоне или планшете Android.', 'Открой Quizball, выбери язык и играй. Выполни вход, когда захочешь играть рейтинговые матчи и сохранять прогресс.'],
    identityTitle: 'Приложение Quizball от quizball.io',
    publisherLabel: 'Издатель',
    packageLabel: 'ID приложения Android',
    questionsTitle: 'Перед установкой',
    questions: [
      { question: 'Что это за приложение Quizball?', answer: 'Это официальное приложение quizball.io для Android, издатель — Quizball LLC. Имя разработчика в Google Play — Quizball, а ID приложения Android — io.quizball.mobile. Нажми кнопку загрузки на этой странице, чтобы открыть нужную страницу приложения.' },
      { question: 'Quizball можно скачать бесплатно?', answer: 'Да. Quizball можно бесплатно скачать из Google Play на поддерживаемые телефоны и планшеты Android. Для игры нужно подключение к интернету.' },
      { question: 'Какие языки можно выбрать?', answer: 'Приложение Quizball поддерживает английский, грузинский, испанский и турецкий языки. Выбери язык в настройках приложения.' },
    ],
  },
  pt: {
    metaTitle: 'Quizball no Google Play — download oficial para Android',
    description: 'Baixe o app oficial de quiz de futebol do Quizball no Google Play. Jogue partidas de perguntas e respostas 1v1, Tiki Taka Toe, Leilão de jogadores e desafios diários no Android.',
    eyebrow: 'O app oficial do Quizball para Android',
    title: 'Quizball para Android.',
    highlight: 'Futebol no seu bolso.',
    storeCta: 'Baixe o Quizball no Google Play',
    homeCta: 'Quizball no Google Play',
    browserCta: 'Jogue no navegador',
    homeLabel: 'Início do Quizball',
    languagesLabel: 'Escolha seu idioma',
    free: 'Download grátis · celulares e tablets Android',
    accountNote: 'Jogue os modos solo disponíveis como convidado no app ou no navegador. Crie uma conta para jogar partidas ranqueadas, ganhar pontos no ranking, jogar no multiplayer e salvar seu progresso.',
    featuresTitle: 'Muito mais que um quiz de futebol',
    features: [
      { title: 'Quiz 1v1 ranqueado', body: 'Responda a perguntas de futebol, enfrente outro jogador e suba no ranking.' },
      { title: 'Tiki Taka Toe e Leilão', body: 'Adivinhe o jogador que se encaixa nas duas categorias ou use as dicas para ganhar um leilão de jogadores.' },
      { title: 'Jogos diários e Party Quiz', body: 'Experimente os desafios diários de futebol ou convide seus amigos para uma sala privada de quiz.' },
    ],
    privacy: 'Política de privacidade', terms: 'Termos de serviço',
    officialNote: 'O app oficial do Quizball, de quizball.io, publicado pela Quizball LLC.',
    rankedScreenshot: '/assets/download/ranked-en.png',
    rankedAlt: 'Partida ranqueada do Quizball em um celular Android: posse de bola, uma pergunta e quatro opções de resposta.',
    rankedCaption: '1v1 ranqueado. Gameplay real no Android.',
    installTitle: 'Como baixar o Quizball no Google Play',
    installSteps: ['Abra a página oficial do Quizball no Google Play pelo botão acima.', 'Confira se o desenvolvedor é Quizball e toque em Instalar no seu celular ou tablet Android.', 'Abra o Quizball, escolha seu idioma e jogue. Entre na sua conta para jogar partidas ranqueadas e salvar seu progresso.'],
    identityTitle: 'O app Quizball de quizball.io',
    publisherLabel: 'Publicado por',
    packageLabel: 'ID do app Android',
    questionsTitle: 'Antes de instalar',
    questions: [
      { question: 'Que app do Quizball é este?', answer: 'Este é o app oficial do quizball.io para Android, publicado pela Quizball LLC. O nome do desenvolvedor no Google Play é Quizball e o ID do app Android é io.quizball.mobile. Use o botão de download desta página para abrir a página correta.' },
      { question: 'O Quizball é grátis para baixar?', answer: 'Sim. Você pode baixar o Quizball gratuitamente no Google Play em celulares e tablets Android compatíveis. É necessário ter conexão com a internet para jogar.' },
      { question: 'Quais idiomas posso escolher?', answer: 'O app do Quizball oferece suporte a inglês, georgiano, espanhol, turco e português do Brasil. Escolha seu idioma nas configurações do app.' },
    ],
  },
  de: {
    metaTitle: 'Quizball bei Google Play — offizieller Android-Download',
    description: 'Lade die offizielle Quizball-App für Fußballquizze bei Google Play herunter. Spiele 1v1-Quiz, Fußball Tic Tac Toe, Fußball-Auktion und Fußball-Tages-Challenges auf Android.',
    eyebrow: 'Die offizielle Quizball-App für Android',
    title: 'Quizball für Android.',
    highlight: 'Fußball in deiner Tasche.',
    storeCta: 'Quizball bei Google Play laden',
    homeCta: 'Quizball bei Google Play',
    browserCta: 'Im Browser spielen',
    homeLabel: 'Quizball-Startseite',
    languagesLabel: 'Sprache wählen',
    free: 'Kostenloser Download · Android-Smartphones und -Tablets',
    accountNote: 'Spiele unterstützte Einzelspielermodi als Gast in der App oder im Browser. Erstelle ein Konto für Ranked-Matches, Ranglistenpunkte, Mehrspielerpartien und gespeicherten Fortschritt.',
    featuresTitle: 'Mehr als ein Fußballquiz',
    features: [
      { title: 'Ranked-Quiz 1v1', body: 'Beantworte Fußballfragen, tritt im direkten Duell an und steige in der Rangliste auf.' },
      { title: 'Fußball Tic Tac Toe & Auktion', body: 'Nenne den Spieler, der zu beiden Kategorien passt, oder nutze Hinweise, um bei der Fußball-Auktion einen Spieler zu verpflichten.' },
      { title: 'Tägliche Spiele & Party Quiz', body: 'Spiele Fußball-Tages-Challenges oder lade Freunde in einen privaten Quizraum ein.' },
    ],
    privacy: 'Datenschutzerklärung', terms: 'Nutzungsbedingungen',
    officialNote: 'Die offizielle Quizball-App von quizball.io, veröffentlicht von Quizball LLC.',
    rankedScreenshot: '/assets/download/ranked-en.png',
    rankedAlt: 'Quizball-Ranked-Spiel auf einem Android-Smartphone: Ballbesitz, eine Frage und vier Antworten.',
    rankedCaption: 'Ranked 1v1. Echtes Android-Gameplay.',
    installTitle: 'Quizball von Google Play herunterladen',
    installSteps: ['Öffne über die Schaltfläche oben den offiziellen Eintrag bei Google Play.', 'Prüfe, ob als Entwickler Quizball angegeben ist, und tippe dann auf deinem Android-Smartphone oder -Tablet auf Installieren.', 'Öffne Quizball, wähle deine Sprache und spiele. Melde dich an, wenn du Ranked-Matches spielen und deinen Fortschritt speichern möchtest.'],
    identityTitle: 'Die Quizball-App von quizball.io',
    publisherLabel: 'Veröffentlicht von',
    packageLabel: 'Android-App-ID',
    questionsTitle: 'Vor der Installation',
    questions: [
      { question: 'Welche Quizball-App ist das?', answer: 'Dies ist die offizielle Android-App von quizball.io, veröffentlicht von Quizball LLC. Der Entwicklername bei Google Play lautet Quizball, die Android-App-ID ist io.quizball.mobile. Öffne über die Download-Schaltfläche auf dieser Seite den richtigen Eintrag.' },
      { question: 'Kann ich Quizball kostenlos herunterladen?', answer: 'Ja. Quizball kann für unterstützte Android-Smartphones und -Tablets kostenlos bei Google Play heruntergeladen werden. Zum Spielen ist eine Internetverbindung erforderlich.' },
      { question: 'Welche Sprachen kann ich auswählen?', answer: 'Die Quizball-App unterstützt Englisch, Georgisch, Spanisch und Türkisch. Wähle deine Sprache in den App-Einstellungen.' },
    ],
  },
};

/** A normal WebPage with a verified install link; no invented ratings or app-rich-result promises. */
export function buildDownloadStructuredData(locale: Locale) {
  const copy = DOWNLOAD_COPY[locale];
  const url = `${SITE_URL}${downloadPath(locale)}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: copy.metaTitle,
    description: copy.description,
    inLanguage: locale,
    image: `${SITE_URL}${SITE_ICON_PATH}`,
    isPartOf: { '@id': SITE_SCHEMA_IDS.website },
    about: { '@id': SITE_SCHEMA_IDS.game },
    publisher: { '@id': SITE_SCHEMA_IDS.organization },
    significantLink: GOOGLE_PLAY_URL,
    dateModified: DOWNLOAD_UPDATED_AT,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: copy.homeLabel, item: `${SITE_URL}/${locale}` },
        { '@type': 'ListItem', position: 2, name: copy.homeCta, item: url },
      ],
    },
  };
}
